import React, { useEffect } from 'react';

/**
 * ConfirmDialog — a small, app-like modal used to protect the user from
 * accidentally losing unsent input. Intentionally generic so it can guard any
 * destructive transition (switching emails, tabs, closing the composer…).
 *
 * Controlled entirely by the parent: render it only when a confirmation is
 * pending. `onCancel` is the safe default (keeps editing); `onConfirm` performs
 * the discarding action.
 */
export default function ConfirmDialog({
  title = 'Discard draft?',
  message = 'You have an unsent reply. If you continue, your text will be lost.',
  confirmLabel = 'Discard',
  cancelLabel = 'Keep editing',
  onConfirm,
  onCancel,
}) {
  // Esc cancels (safe), Enter confirms — keyboard-friendly without trapping.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel();
      else if (e.key === 'Enter') onConfirm();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onConfirm, onCancel]);

  return (
    <div className="ivx-confirm-overlay" onClick={onCancel}>
      <div
        className="ivx-confirm"
        role="alertdialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="ivx-confirm-title">{title}</h3>
        <p className="ivx-confirm-message">{message}</p>
        <div className="ivx-confirm-actions">
          <button type="button" className="ivx-confirm-cancel" onClick={onCancel} autoFocus>
            {cancelLabel}
          </button>
          <button type="button" className="ivx-confirm-confirm" onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
